/**
 * Capa 4: Gestor de Estado Global del Onboarding — Zustand
 * Mantiene la reactividad del wizard, sincronización de pasos y dirección de animación.
 */

import { create } from 'zustand';
import {
  Paso1GranjaFormData,
  Paso2UsuarioFormData,
  Paso3CredencialesFormData,
} from '../types/onboarding.types';

export interface ValidationState {
  checking: boolean;
  available: boolean;
  suggestLogin: boolean;
  message: string | null;
}

interface OnboardingState {
  idOnboarding: string | null;
  pasoActual: number; // 1, 2 o 3
  direccionAnimacion: number; // 1 para adelante, -1 para atrás

  paso1: Paso1GranjaFormData;
  paso2: Paso2UsuarioFormData;
  paso3: Paso3CredencialesFormData;

  emailValidation: ValidationState;
  telefonoValidation: ValidationState;

  isInitializing: boolean;
  isSubmitting: boolean;

  // Acciones
  setIdOnboarding: (id: string | null) => void;
  setPasoActual: (paso: number, direccion?: number) => void;
  updatePaso1: (data: Partial<Paso1GranjaFormData>) => void;
  updatePaso2: (data: Partial<Paso2UsuarioFormData>) => void;
  updatePaso3: (data: Partial<Paso3CredencialesFormData>) => void;
  setEmailValidation: (val: Partial<ValidationState>) => void;
  setTelefonoValidation: (val: Partial<ValidationState>) => void;
  setIsInitializing: (val: boolean) => void;
  setIsSubmitting: (val: boolean) => void;
  resetOnboarding: () => void;
}

const initialPaso1: Paso1GranjaFormData = {
  nombre_granja: '',
  pais: 'Colombia',
  departamento_estado: '',
  municipio_ciudad: '',
  idioma: 'es',
  capacidad_total_aves: null,
  sistema_alojamiento: '',
};

const initialPaso2: Paso2UsuarioFormData = {
  primer_nombre: '',
  segundo_nombre: '',
  primer_apellido: '',
  segundo_apellido: '',
  whatsapp: '',
  codigo_pais_whatsapp: '+57',
  email: '',
};

const initialPaso3: Paso3CredencialesFormData = {
  email: '',
  telefono_movil: '',
  password: '',
  confirm_password: '',
  acepta_terminos_y_condiciones: false,
};

const initialValidationState: ValidationState = {
  checking: false,
  available: true,
  suggestLogin: false,
  message: null,
};

const STORAGE_SESSION_ID_KEY = 'avifacil_onboarding_id';

export const useOnboardingStore = create<OnboardingState>((set) => ({
  idOnboarding: typeof window !== 'undefined' ? sessionStorage.getItem(STORAGE_SESSION_ID_KEY) : null,
  pasoActual: 1,
  direccionAnimacion: 1,

  paso1: initialPaso1,
  paso2: initialPaso2,
  paso3: initialPaso3,

  emailValidation: initialValidationState,
  telefonoValidation: initialValidationState,

  isInitializing: false,
  isSubmitting: false,

  setIdOnboarding: (id) => {
    if (typeof window !== 'undefined') {
      if (id) {
        sessionStorage.setItem(STORAGE_SESSION_ID_KEY, id);
      } else {
        sessionStorage.removeItem(STORAGE_SESSION_ID_KEY);
      }
    }
    set({ idOnboarding: id });
  },

  setPasoActual: (paso, direccion = 1) =>
    set({
      pasoActual: paso,
      direccionAnimacion: direccion,
    }),

  updatePaso1: (data) =>
    set((state) => ({
      paso1: { ...state.paso1, ...data },
    })),

  updatePaso2: (data) =>
    set((state) => ({
      paso2: { ...state.paso2, ...data },
    })),

  updatePaso3: (data) =>
    set((state) => ({
      paso3: { ...state.paso3, ...data },
    })),

  setEmailValidation: (val) =>
    set((state) => ({
      emailValidation: { ...state.emailValidation, ...val },
    })),

  setTelefonoValidation: (val) =>
    set((state) => ({
      telefonoValidation: { ...state.telefonoValidation, ...val },
    })),

  setIsInitializing: (isInitializing) => set({ isInitializing }),
  setIsSubmitting: (isSubmitting) => set({ isSubmitting }),

  resetOnboarding: () => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(STORAGE_SESSION_ID_KEY);
    }
    set({
      idOnboarding: null,
      pasoActual: 1,
      direccionAnimacion: 1,
      paso1: initialPaso1,
      paso2: initialPaso2,
      paso3: initialPaso3,
      emailValidation: initialValidationState,
      telefonoValidation: initialValidationState,
      isInitializing: false,
      isSubmitting: false,
    });
  },
}));
